import AsyncStorage from '@react-native-async-storage/async-storage';

const LEGACY_PRINTERS_KEY = 'printers';
const LEGACY_PRINTER_EVENTS_KEY = 'printerEvents';
const SECURE_PRINTER_SETTINGS_KEY = 'secure_printer_settings_v1';
// Dedicated, durable store for printer PAIRINGS ({ [eventKey]: {printers,type,name} }).
// Pairings ("which physical printer does this profile print to") are local-only
// and must survive every profile refresh/edit. Keeping them in their own store —
// the single source of truth applied as an overlay onto `event.printers` — means
// a stale profile array (server re-sync, Redux desync, a bad merge) can never
// silently drop the binding. See savePrinterSettings / applyPairingMap below.
const SECURE_PAIRINGS_KEY = 'secure_printer_pairings_v1';
const LEGACY_PAIRINGS_KEY = 'printerPairings';

let _secureStoreModule = null;

const getSecureStore = () => {
    if (_secureStoreModule !== null) {
        return _secureStoreModule;
    }

    try {
        // Lazy require keeps web/electron builds from hard failing.
        _secureStoreModule = require('expo-secure-store');
    } catch (_) {
        _secureStoreModule = null;
    }

    return _secureStoreModule;
};

const safeParse = (value, fallback) => {
    if (!value) return fallback;
    try {
        return JSON.parse(value);
    } catch (_) {
        return fallback;
    }
};

const normalizePrinterSettings = (payload = {}) => {
    const printers = Array.isArray(payload?.printers) ? payload.printers : [];
    const printerEvents = Array.isArray(payload?.printerEvents) ? payload.printerEvents : [];

    return { printers, printerEvents };
};

// ---------------------------------------------------------------------------
// Pairing map — pure helpers (unit-tested in app/tests/securePrinterStorage.test.js)
// ---------------------------------------------------------------------------

/**
 * Stable identity of a printer event for pairing lookup: Mongo `_id` first, then
 * a client-side `id`, then a `type::name` fallback (covers a freshly created
 * profile whose temporary id later becomes a server id).
 *
 * @param {object} event a printerEvent
 * @returns {string} the pairing key, or '' when none can be derived
 */
export const pairingKey = (event) => {
    const id = event?._id || event?.id;
    if (id) return String(id);
    if (event?.type && event?.name) return `tn:${event.type}::${event.name}`;

    return '';
};

const hasPairing = (event) => Array.isArray(event?.printers) && event.printers.length > 0;

/**
 * Learn pairings from any events that carry a non-empty `printers` array. This
 * only ever ADDS/updates entries — it never clears one — so a write that doesn't
 * know about pairings (a server refresh, a stale Redux copy) cannot erase the
 * map. Clearing a pairing is the exclusive job of {@link savePrinterPairing}.
 *
 * @param {object[]} events printerEvents to learn from
 * @param {object} [map] the current pairing map
 * @returns {object} the next pairing map
 */
export const learnPairingsFromEvents = (events = [], map = {}) => {
    const next = { ...(map || {}) };
    (Array.isArray(events) ? events : []).forEach((event) => {
        if (!hasPairing(event)) return;
        const key = pairingKey(event);
        if (!key) return;
        next[key] = { printers: event.printers, type: event.type, name: event.name };
    });

    return next;
};

/**
 * Resolve the durable pairing for an event: by stable key first, then by
 * `type` + `name` (handles a temp-id → server-id transition).
 *
 * @param {object} event a printerEvent
 * @param {object} [map] the pairing map
 * @returns {object[]|null} the paired printers, or null when none is stored
 */
export const resolvePairing = (event, map = {}) => {
    if (!event || !map) return null;
    const key = pairingKey(event);
    if (key && map[key] && Array.isArray(map[key].printers)) {
        return map[key].printers;
    }
    const entry = Object.values(map).find(
        (e) => e && e.type === event.type && e.name === event.name && Array.isArray(e.printers),
    );

    return entry ? entry.printers : null;
};

/**
 * Overlay the pairing map onto a list of events so each `event.printers` reflects
 * the durable binding. Events absent from the map keep whatever `printers` they
 * already carry (normalized to an array), so an in-flight pairing is not lost
 * before it is learned.
 *
 * @param {object[]} events printerEvents (typically fresh profile data)
 * @param {object} [map] the pairing map
 * @returns {object[]} events with `printers` overlaid
 */
export const applyPairingMap = (events = [], map = {}) =>
    (Array.isArray(events) ? events : []).map((event) => {
        const printers = resolvePairing(event, map);
        if (printers) {
            return { ...event, printers };
        }

        return { ...event, printers: Array.isArray(event?.printers) ? event.printers : [] };
    });

// ---------------------------------------------------------------------------
// Pairing map — storage
// ---------------------------------------------------------------------------

/** SecureStore rejects (and warns about) values above 2048 bytes. */
const SECURE_STORE_MAX_CHARS = 2000;

/**
 * Writes to SecureStore only when the value fits. Oversized payloads live in the
 * AsyncStorage copy alone, and any older (smaller) secure copy is deleted so the
 * secure-first read path can never return stale data.
 */
const setSecureOrClear = async (secureStore, key, value) => {
    if (value.length <= SECURE_STORE_MAX_CHARS) {
        await secureStore.setItemAsync(key, value);

        return;
    }

    if (typeof secureStore.deleteItemAsync === 'function') {
        await secureStore.deleteItemAsync(key);
    }
};

const readPairingMap = async () => {
    const secureStore = getSecureStore();
    if (typeof secureStore?.getItemAsync === 'function') {
        try {
            const raw = await secureStore.getItemAsync(SECURE_PAIRINGS_KEY);
            const parsed = safeParse(raw, null);
            if (parsed && typeof parsed === 'object') {
                return parsed;
            }
        } catch (_) {
            // Secure storage unavailable – fall back to the legacy key.
        }
    }

    const legacy = await AsyncStorage.getItem(LEGACY_PAIRINGS_KEY);

    return safeParse(legacy, {}) || {};
};

const writePairingMap = async (map = {}) => {
    const serialized = JSON.stringify(map || {});
    const secureStore = getSecureStore();
    if (secureStore?.setItemAsync) {
        try {
            await setSecureOrClear(secureStore, SECURE_PAIRINGS_KEY, serialized);
        } catch (_) {
            // Non-fatal: legacy AsyncStorage copy below is still authoritative.
        }
    }
    await AsyncStorage.setItem(LEGACY_PAIRINGS_KEY, serialized);
};

// ---------------------------------------------------------------------------
// Printer settings — read/write primitives
// ---------------------------------------------------------------------------

const readSettingsRaw = async () => {
    const secureStore = getSecureStore();

    // Some platforms (web/electron) expose the module but not a usable
    // getItemAsync, or throw when called. Treat any failure as "no secure
    // value" and fall back to the legacy AsyncStorage keys below.
    if (typeof secureStore?.getItemAsync === 'function') {
        try {
            const secureRaw = await secureStore.getItemAsync(SECURE_PRINTER_SETTINGS_KEY);
            const secureParsed = safeParse(secureRaw, null);
            if (secureParsed) {
                return normalizePrinterSettings(secureParsed);
            }
        } catch (_) {
            // Secure storage unavailable on this platform – use legacy keys.
        }
    }

    const [legacyPrintersRaw, legacyEventsRaw] = await AsyncStorage.multiGet([
        LEGACY_PRINTERS_KEY,
        LEGACY_PRINTER_EVENTS_KEY,
    ]);

    return normalizePrinterSettings({
        printers: safeParse(legacyPrintersRaw?.[1], []),
        printerEvents: safeParse(legacyEventsRaw?.[1], []),
    });
};

const writeSettingsRaw = async ({ printers = [], printerEvents = [] } = {}) => {
    const normalized = normalizePrinterSettings({ printers, printerEvents });
    const serialized = JSON.stringify({
        ...normalized,
        updatedAt: new Date().toISOString(),
        schemaVersion: 1,
    });

    const secureStore = getSecureStore();
    if (secureStore?.setItemAsync) {
        try {
            await setSecureOrClear(secureStore, SECURE_PRINTER_SETTINGS_KEY, serialized);
        } catch (_) {
            // Non-fatal: keep the legacy AsyncStorage copy in sync below.
        }
    }

    // Keep legacy keys in sync for backward compatibility (the print path reads
    // AsyncStorage `printerEvents` directly).
    await AsyncStorage.multiSet([
        [LEGACY_PRINTERS_KEY, JSON.stringify(normalized.printers)],
        [LEGACY_PRINTER_EVENTS_KEY, JSON.stringify(normalized.printerEvents)],
    ]);
};

// ---------------------------------------------------------------------------
// Serialized, pairing-preserving public API
// ---------------------------------------------------------------------------

// All persistence is funneled through a single promise chain so concurrent
// callers can never interleave the read-modify-write of the pairing overlay and
// clobber each other. Every link swallows its own error (see the wrappers) so a
// single failure can't wedge the queue.
let _writeChain = Promise.resolve();

const enqueueWrite = (task) => {
    const run = () => task();
    _writeChain = _writeChain.then(run, run);

    return _writeChain;
};

/**
 * Persist printer settings (physical printers + profile events) durably to
 * SecureStore + legacy AsyncStorage, PRESERVING every profile's device pairing.
 *
 * Guarantees, regardless of which (possibly buggy) caller invokes it:
 *  - Pairings are re-applied from the durable pairing map, so a stale/refetched
 *    profile array that dropped `event.printers` cannot erase the binding.
 *  - A non-empty stored profile list is never overwritten by an empty incoming
 *    one (matches the SystemContext guard, enforced at the storage chokepoint).
 *  - Writes are serialized to avoid read-modify-write races.
 *
 * @param {{printers?: object[], printerEvents?: object[]}} [payload]
 * @returns {Promise<void>}
 */
export const savePrinterSettings = (payload) => enqueueWrite(async () => {
    try {
        const incoming = normalizePrinterSettings(payload || {});
        const persisted = await readSettingsRaw();

        // Never wipe a non-empty configuration with an empty incoming one.
        const events = incoming.printerEvents.length > 0 ? incoming.printerEvents : persisted.printerEvents;
        const printers = incoming.printers.length > 0 ? incoming.printers : persisted.printers;

        // Learn pairings from both what is stored and what is incoming, then
        // overlay so the persisted `event.printers` always reflect the map.
        let map = await readPairingMap();
        map = learnPairingsFromEvents(persisted.printerEvents, map);
        map = learnPairingsFromEvents(events, map);
        const overlaidEvents = applyPairingMap(events, map);

        await writePairingMap(map);
        await writeSettingsRaw({ printers, printerEvents: overlaidEvents });
    } catch (e) {
        console.warn('[securePrinterStorage] savePrinterSettings failed:', e?.message);
    }
});

/**
 * Authoritatively set (or CLEAR) a single profile's pairing. This is the only
 * path allowed to remove a binding, so the pairing screen's unpair works while
 * generic writes stay non-destructive. Updates the durable map and re-derives
 * the persisted events so the print path sees the change immediately.
 *
 * @param {object} event the printerEvent being (un)paired
 * @param {object[]} printers the printers to bind (pass [] to unpair)
 * @returns {Promise<void>}
 */
export const savePrinterPairing = (event, printers = []) => enqueueWrite(async () => {
    try {
        const key = pairingKey(event);
        if (!key) return;

        const map = await readPairingMap();
        map[key] = {
            printers: Array.isArray(printers) ? printers : [],
            type: event?.type,
            name: event?.name,
        };
        await writePairingMap(map);

        const persisted = await readSettingsRaw();
        const overlaidEvents = applyPairingMap(persisted.printerEvents, map);
        await writeSettingsRaw({ printers: persisted.printers, printerEvents: overlaidEvents });
    } catch (e) {
        console.warn('[securePrinterStorage] savePrinterPairing failed:', e?.message);
    }
});

/**
 * Load printer settings with pairings overlaid. On first run for an existing
 * install (empty map, stored events already carry `printers`) the map is seeded
 * from those events so the migration is transparent.
 *
 * @returns {Promise<{printers: object[], printerEvents: object[]}>}
 */
export const loadPrinterSettings = async () => {
    const raw = await readSettingsRaw();

    let map = await readPairingMap();
    if (Object.keys(map).length === 0) {
        const seeded = learnPairingsFromEvents(raw.printerEvents, map);
        if (Object.keys(seeded).length > 0) {
            map = seeded;
            try {
                await writePairingMap(map);
            } catch (_) {
                // Seeding is best-effort; the overlay below still applies in-memory.
            }
        }
    }

    return {
        printers: raw.printers,
        printerEvents: applyPairingMap(raw.printerEvents, map),
    };
};

/**
 * Load only the durable pairing map (used by SystemContext to overlay pairings
 * synchronously onto in-memory profile updates).
 *
 * @returns {Promise<object>} the pairing map
 */
export const loadPrinterPairings = async () => readPairingMap();
